import { z } from 'zod'
import { type Action, type Actor, can, ROLES } from './permissions.ts'

export const documentGrantSchema = z
  .object({
    subject: z.enum(['organization', 'person', 'role']),
    subjectId: z.string().min(1),
    level: z.enum(['read', 'edit']),
  })
  .strict()
  .refine(
    (value) =>
      value.subject === 'organization'
        ? value.subjectId === 'organization'
        : value.subject !== 'role' || ROLES.some((role) => role === value.subjectId),
    'Оберіть організацію, працівника або роль',
  )
export type DocumentGrant = z.infer<typeof documentGrantSchema>
const grants = z
  .array(documentGrantSchema)
  .max(200)
  .refine(
    (items) =>
      new Set(items.map((item) => `${item.subject}:${item.subjectId}`)).size === items.length,
    'Доступ для цього учасника вже додано',
  )
export interface InheritedDocumentGrant extends DocumentGrant {
  sourceId: string | null
  sourceName: string | null
}

const name = z
  .string()
  .trim()
  .min(1, 'Вкажіть назву')
  .max(255, 'Назва має містити до 255 символів')
  .refine(
    (value) =>
      Array.from(value).every(
        (character) => character.charCodeAt(0) >= 32 && !'/\\'.includes(character),
      ),
    'Назва містить недопустимі символи',
  )
export const documentCreateSchema = z
  .object({
    kind: z.enum(['file', 'folder']),
    name,
    parentId: z.string().min(1).nullable().optional(),
    shared: z.boolean().default(false),
    grants: grants.optional(),
    storedObjectId: z.string().min(1).optional(),
  })
  .strict()
  .refine(
    (value) => (value.kind === 'folder' ? !value.storedObjectId : Boolean(value.storedObjectId)),
    'Оберіть файл',
  )
export const documentUpdateSchema = z
  .object({
    id: z.string().min(1),
    version: z.number().int().positive(),
    name: name.optional(),
    parentId: z.string().min(1).nullable().optional(),
    shared: z.boolean().optional(),
    grants: grants.optional(),
  })
  .strict()
export const documentDeleteSchema = z
  .object({ id: z.string().min(1), version: z.number().int().positive() })
  .strict()
export type DocumentCreateInput = z.infer<typeof documentCreateSchema>
export type DocumentUpdateInput = z.infer<typeof documentUpdateSchema>
export type DocumentDeleteInput = z.infer<typeof documentDeleteSchema>

export interface DocumentItem {
  id: string
  ownerId: string
  ownerName: string
  parentId: string | null
  kind: 'file' | 'folder'
  name: string
  shared: boolean
  effectiveShared: boolean
  storedObjectId: string | null
  sizeBytes: string
  contentType: string
  version: number
  createdAt: string
  updatedAt: string
  canManage: boolean
  canShare: boolean
  canEdit: boolean
  canCreate: boolean
  accessLevel: 'read' | 'edit' | 'owner'
  grants: DocumentGrant[]
  inheritedGrants: InheritedDocumentGrant[]
  sharedByName: string | null
}
export interface DocumentUsageLink {
  kind: string
  label: string
  href: string
}
export interface StorageUsageFile {
  storedObjectId: string
  name: string
  sizeBytes: string
  category: 'documents' | 'attachments'
  documentId: string | null
  href: string | null
}
export interface StorageUsageDetail extends StorageUsageUser {
  largestFiles: StorageUsageFile[]
}
export interface StorageUsageUser {
  userId: string
  totalBytes: string
  fileCount: number
  documentsBytes: string
  documentsCount: number
  attachmentsBytes: string
  attachmentsCount: number
}
export interface StorageUsage {
  users: StorageUsageUser[]
  totalBytes: string
  fileCount: number
}
export const canDocument = (
  actor: Actor | null | undefined,
  action: Action,
  item?: Pick<DocumentItem, 'ownerId' | 'effectiveShared'> &
    Partial<Pick<DocumentItem, 'accessLevel'>>,
): boolean => {
  if (!actor || !can(actor, action, 'documents')) return false
  if (action === 'create')
    return (
      !item ||
      item.ownerId === actor.id ||
      (actor.role === 'admin' && item.effectiveShared) ||
      item.accessLevel === 'edit'
    )
  if (!item || (item.ownerId !== actor.id && !item.effectiveShared)) return false
  return (
    action === 'read' ||
    item.ownerId === actor.id ||
    actor.role === 'admin' ||
    (action === 'update' && item.accessLevel === 'edit')
  )
}
